import type { WorkerScheduleItem } from 'admin/src/types/Pollworker/LocationStaffing';
import { normalizeWorkDate } from 'admin/src/utils/pollworker/scheduleDates';

export type SameDayScheduleRef = {
  evUserId: string;
  date: string;
  locationId: string;
  locationName: string;
  roleId: string;
  roleName: string;
};

export function toSameDayScheduleRef(schedule: WorkerScheduleItem): SameDayScheduleRef {
  return {
    evUserId: schedule.evUser.id,
    date: normalizeWorkDate(schedule.workDate),
    locationId: schedule.locationId,
    locationName: schedule.locationName,
    roleId: schedule.roleId,
    roleName: schedule.roleName,
  };
}

function toScheduleRef(schedule: SameDayScheduleRef | WorkerScheduleItem): SameDayScheduleRef {
  if ('evUser' in schedule) return toSameDayScheduleRef(schedule);
  return {
    ...schedule,
    date: normalizeWorkDate(schedule.date),
  };
}

export function buildSameDaySchedulesByWorker(schedules: Array<SameDayScheduleRef | WorkerScheduleItem>, date: string): Map<string, SameDayScheduleRef[]> {
  const normalizedDate = normalizeWorkDate(date);
  const byWorker = new Map<string, SameDayScheduleRef[]>();

  for (const schedule of schedules) {
    const ref = toScheduleRef(schedule);
    if (ref.date !== normalizedDate) continue;

    const existing = byWorker.get(ref.evUserId);
    if (existing) {
      existing.push(ref);
    } else {
      byWorker.set(ref.evUserId, [ref]);
    }
  }

  return byWorker;
}

export function getWorkerSchedulesOnDate(schedules: Array<SameDayScheduleRef | WorkerScheduleItem>, evUserId: string, date: string): SameDayScheduleRef[] {
  return buildSameDaySchedulesByWorker(schedules, date).get(evUserId) || [];
}

export function getSameDayScheduleCount(schedules: Array<SameDayScheduleRef | WorkerScheduleItem>, evUserId: string, date: string): number {
  return getWorkerSchedulesOnDate(schedules, evUserId, date).length;
}

export function formatSameDayScheduleLabels(schedules: SameDayScheduleRef[]): string[] {
  const groups = new Map<string, { label: string; count: number }>();

  for (const schedule of schedules) {
    const key = `${schedule.roleId}|${schedule.locationId}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      continue;
    }
    groups.set(key, {
      label: `${schedule.roleName} @ ${schedule.locationName}`,
      count: 1,
    });
  }

  return [...groups.values()].map(({ label, count }) => (count > 1 ? `${label} ×${count}` : label));
}

export function buildSameDayScheduleCountByWorker(schedules: WorkerScheduleItem[], date: string): Map<string, number> {
  const byWorker = buildSameDaySchedulesByWorker(schedules, date);
  const counts = new Map<string, number>();

  for (const [evUserId, workerSchedules] of byWorker) {
    counts.set(evUserId, workerSchedules.length);
  }

  return counts;
}
